// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useSettingsStore } from '@/store/settingsStore';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

// Components
import { OptimizedTransactionTable } from './components/OptimizedTransactionTable';
import { OptimizedMobileTransactionView } from './components/OptimizedMobileTransactionView';
import { ColumnsRecord } from './components/TransactionTableHeader';
import EditTransaction from './EditTransaction';
import { MassUpdateTransactions } from './MassUpdateTransactions';
import ColumnSettings from './ColumnSettings';
import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

// Hooks and Utils
import { useTransactionData } from './hooks/useTransactionData';
import { LocalTransaction } from './utils/TransactionFormatters';
import { useTransactionStore as useCoreTransactionStore } from '@flows.cash/core';
import { useTransactionStore } from '@/store/transactionsStore';




interface Props {
  accountId?: string;
  onRefresh?: () => void;
}

// Default column settings to match ColumnSettings component
const defaultColumns: ColumnsRecord = {
  date: { id: 'date', label: 'Date', visible: true, width: 120, sortable: true },
  description: { id: 'description', label: 'Description', visible: true, width: 200, filterable: true },
  category: { id: 'category', label: 'Category', visible: true, width: 150, filterable: true },
  amount: { id: 'amount', label: 'Amount', visible: true, width: 120, sortable: true },
  account: { id: 'account', label: 'Account', visible: true, width: 150, filterable: true },
  payee: { id: 'payee', label: 'Payee', visible: false, width: 150, filterable: true },
  status: { id: 'status', label: 'Status', visible: false, width: 120, filterable: true },
  notes: { id: 'notes', label: 'Notes', visible: false, width: 200, filterable: true },
  tags: { id: 'tags', label: 'Tags', visible: false, width: 150, filterable: true }
};

const defaultColumnOrder = ['date', 'description', 'category', 'amount', 'account', 'payee', 'status', 'notes', 'tags'];

// Column resizing styles
const resizableStyles = `
  /* Main wrapper */
  .transactions-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    position: relative;
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .dark .transactions-container {
    background: hsl(var(--background));
  }

  /* Header area */
  .transactions-header {
    flex-shrink: 0;
    padding: 1rem 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* Main table area */
  .transactions-table-wrapper {
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .transactions-table-container {
    border-radius: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    background: white;
    position: relative;
    min-height: 0;
  }

  .dark .transactions-table-container {
    background: hsl(var(--background));
  }

  .transactions-scroll-container {
    flex: 1;
    overflow: auto !important;
    position: relative;
    -webkit-overflow-scrolling: touch;
    min-height: 0;
  }

  /* Table specific styles */
  .transactions-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: fixed;
  }

  /* Header row styling */
  .transactions-header-row {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: #f9f9f9;
  }

  .dark .transactions-header-row {
    background-color: #182438;
  }

  /* Checkbox column */
  .checkbox-column {
    width: 48px;
    min-width: 48px;
    max-width: 48px;
    padding: 0.75rem;
    text-align: center;
    position: sticky;
    left: 0;
    z-index: 20;
    background-color: inherit;
  }

  /* Action column */
  .action-column {
    position: sticky !important;
    right: 0 !important;
    width: 60px !important;
    min-width: 60px !important;
    max-width: 60px !important;
    padding: 8px !important;
    z-index: 30 !important;
    text-align: right !important;
    background-color: inherit;
  }

  /* Header cells */
  .table-header-cell {
    font-weight: 500 !important;
    white-space: nowrap !important;
    position: relative;
    padding: 0.75rem 1rem;
    text-align: left;
    color: hsl(var(--foreground));
    background-color: #f9f9f9;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    font-size: 0.875rem;
  }

  .dark .table-header-cell {
    background-color: #182438;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: #E8E8E8;
  }

  /* Cell resizing */
  .resizable-cell {
    position: relative !important;
    min-width: 120px;
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .resizer {
    position: absolute !important;
    right: -2px !important;
    top: 0 !important;
    height: 100% !important;
    width: 6px !important;
    cursor: col-resize !important;
    z-index: 100 !important;
    touch-action: none !important;
    background-color: transparent;
    transition: background-color 0.15s ease;
  }

  .resizer::after {
    content: '';
    position: absolute;
    right: 2px;
    top: 15%;
    height: 70%;
    width: 2px;
    background-color: rgba(0, 0, 0, 0.1);
    transition: background-color 0.15s ease, width 0.15s ease;
  }

  .dark .resizer::after {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .resizer:hover::after,
  .resizer.resizing::after {
    background-color: hsl(var(--primary));
    width: 3px;
  }

  .resizer:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .dark .resizer:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  /* Footer */
  .transactions-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    background-color: #f9f9f9;
    font-size: 0.875rem;
    color: hsl(var(--muted-foreground));
  }

  .dark .transactions-footer {
    background-color: #182438;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  /* Row styling */
  .transaction-row {
    transition: background-color 0.15s ease;
  }

  .transaction-row:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  .dark .transaction-row:hover {
    background-color: rgba(255, 255, 255, 0.02);
  }

  .transaction-row.cursor-pointer:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .dark .transaction-row.cursor-pointer:hover {
    background-color: rgba(255, 255, 255, 0.04);
  }

  .transaction-row.selected {
    background-color: rgba(59, 130, 246, 0.05);
  }

  .dark .transaction-row.selected {
    background-color: rgba(59, 130, 246, 0.1);
  }

  /* Checkbox hover styling */
  .transaction-row:hover .checkbox-column [data-state="unchecked"] {
    opacity: 0.7 !important;
  }

  .transaction-row .checkbox-column [data-state="unchecked"]:hover {
    opacity: 1 !important;
  }

  /* Category and tag styling */
  .category-tag {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }

  /* Account indicator */
  .account-indicator {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  /* Loading spinner */
  .loading-spinner {
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Group header styling */
  .group-header td {
    background-color: rgba(0, 0, 0, 0.02) !important;
    font-weight: 600;
    color: hsl(var(--foreground));
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .dark .group-header td {
    background-color: rgba(255, 255, 255, 0.02) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

export const TransactionList: React.FC<Props> = ({ accountId, onRefresh }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useAuthStore();
  const { settings, updateSettings } = useSettingsStore();
  const coreTransactionStore = useCoreTransactionStore();
  const { fetchTransactions } = useTransactionStore();
  
  // Column management - get from settings store to match ColumnSettings
  const baseTransactionColumns: ColumnsRecord = settings?.preferences?.transactionColumns || defaultColumns;
  
  // Adjust account column visibility based on context (hide when viewing specific account)
  const transactionColumns: ColumnsRecord = {
    ...baseTransactionColumns,
    account: {
      ...baseTransactionColumns.account,
      visible: accountId ? false : baseTransactionColumns.account?.visible !== false
    }
  };
  
  const columnOrder: string[] = settings?.preferences?.columnOrder || defaultColumnOrder;
  
  // Column resizing state (declare early so it can be used in columnWidths calculation)
  const [isResizing, setIsResizing] = useState(false);
  
  // Get column widths from settings store (with fallback to localStorage for migration)
  const [localColumnWidths, setLocalColumnWidths] = useLocalStorage<Record<string, string>>('transactionColumnWidths', {});
  
  // Memoize the settings widths to prevent recalculation on every render
  const settingsWidths = useMemo(() => {
    return (settings?.preferences as any)?.columnWidths || {};
  }, [settings?.preferences]);
  
  // Memoize column widths calculation to prevent infinite re-renders
  const columnWidths = useMemo(() => {
    if (isResizing) {
      return { ...settingsWidths, ...localColumnWidths }; // During resize: local overrides settings
    }
    return Object.keys(settingsWidths).length > 0 ? 
      { ...settingsWidths, ...localColumnWidths } : // Normal: merge settings with local
      localColumnWidths;
  }, [isResizing, settingsWidths, localColumnWidths]);
  
  // Function to update column widths in settings store
  const updateColumnWidths = useCallback((newWidths: Record<string, string>) => {
    const userId = user?.id;
    if (!userId) return;
    
    updateSettings(userId, {
      preferences: {
        ...settings?.preferences,
        columnWidths: newWidths
      } as any
    }).catch(err => console.error('Failed to update column widths:', err));
  }, [user?.id, updateSettings]); // Remove settings?.preferences to prevent circular dependency
  const [groupBy, setGroupBy] = useState<string | null>(null);

  // UI state
  const [editingTransaction, setEditingTransaction] = useState<LocalTransaction | null>(null);
  const [showMassUpdate, setShowMassUpdate] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{
    isDeleting: boolean;
    current: number;
    total: number;
    errors: string[];
  } | null>(null);
  


  // Track initialization to prevent infinite loops
  const hasInitializedRef = useRef(false);
  
  // Store current resize state in refs to avoid stale closures
  const resizeStateRef = useRef({
    isResizing: false,
    resizingColumn: null as string | null,
    startX: 0,
    startWidth: 0,
    currentWidth: 0
  });

  // Use custom hook for transaction data management
  const {
    transactions,
    totalItems,
    isLoading,
    hasMoreData,
    pageSize,
    sortField,
    sortDirection,
    isColumnSorting,
    selectedTransactions,
    accounts,
    categories,
    tags,
    handleSort,
    toggleTransactionSelection,
    toggleAllTransactions,
    setSelectedTransactions,
    loadMore,
    refreshTransactions
  } = useTransactionData({ accountId });

  const loadingMoreText = t('transactions.loadingMore', 'Loading more transactions...');

  // Initialize default settings if not set
  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    // Initialize column settings if missing
    if (!settings?.preferences?.transactionColumns || !settings?.preferences?.columnOrder) {
      updateSettings(userId, {
        preferences: {
          ...settings?.preferences,
          transactionColumns: settings?.preferences?.transactionColumns || defaultColumns,
          columnOrder: settings?.preferences?.columnOrder || defaultColumnOrder
        }
      }).catch(err => console.error('Failed to initialize column settings:', err));
    }
  }, [settings?.preferences?.transactionColumns, settings?.preferences?.columnOrder, user?.id, updateSettings]);

  // Separate effect for column width initialization to avoid circular dependency
  useEffect(() => {
    const userId = user?.id;
    if (!userId || hasInitializedRef.current) return;

    const currentSettingsWidths = (settings?.preferences as any)?.columnWidths || {};
    const hasSettingsWidths = Object.keys(currentSettingsWidths).length > 0;
    const hasLocalWidths = Object.keys(localColumnWidths).length > 0;

    // Only initialize if we don't have any widths set
    if (!hasSettingsWidths && !hasLocalWidths) {
      const defaultWidths = {
        date: '120px',
        description: '200px',
        category: '150px',
        amount: '120px',
        account: '150px',
        payee: '150px',
        notes: '200px',
        tags: '150px',
        status: '100px'
      };
      updateColumnWidths(defaultWidths);
      hasInitializedRef.current = true;
    }
    // Migrate from localStorage to settings store if needed
    else if (hasLocalWidths && !hasSettingsWidths) {
      updateColumnWidths(localColumnWidths);
      setLocalColumnWidths({});  // Clear localStorage after migration
      hasInitializedRef.current = true;
    }
    else if (hasSettingsWidths) {
      // Mark as initialized if settings already exist
      hasInitializedRef.current = true;
    }
  }, [user?.id]); // Only depend on user?.id to prevent frequent re-runs

  // Column resizing handlers - using refs to avoid stale closures
  const handleResizeMove = useCallback((e: MouseEvent) => {
    const state = resizeStateRef.current;
    if (!state.isResizing || !state.resizingColumn) return;

    const diff = e.clientX - state.startX;
    const newWidth = Math.max(80, state.startWidth + diff);
    const newWidthPx = `${newWidth}px`;
    
    // Store the current width in the ref for later use
    resizeStateRef.current.currentWidth = newWidth;
    
    // Update local state immediately for smooth resizing, but don't persist yet
    setLocalColumnWidths(prev => {
      const newWidths = {
        ...prev,
        [state.resizingColumn!]: newWidthPx
      };
      // Console log occasionally to see the updates (every 10px to avoid spam)
      if (Math.abs(newWidth - state.startWidth) % 10 < 2) {
        console.log('Resizing', state.resizingColumn, 'to', newWidthPx);
      }
      return newWidths;
    });
  }, [setLocalColumnWidths]);

  const handleResizeEnd = useCallback(() => {
    const state = resizeStateRef.current;
    if (state.isResizing && state.resizingColumn && state.currentWidth > 0) {
      console.log('Resize ended for:', state.resizingColumn);
      
      const newWidthPx = `${state.currentWidth}px`;
      console.log('Final width to save:', newWidthPx);
      
      // Get current widths from localStorage and settings to avoid stale closure
      const currentLocalWidths = JSON.parse(localStorage.getItem('transactionColumnWidths') || '{}');
      const currentSettingsWidths = (settings?.preferences as any)?.columnWidths || {};
      
      const finalWidths = {
        ...currentSettingsWidths,
        ...currentLocalWidths,
        [state.resizingColumn]: newWidthPx
      };
      
      console.log('Saving final widths:', finalWidths);
      updateColumnWidths(finalWidths);
    }
    
    // Reset state
    setIsResizing(false);
    
    // Reset ref state
    resizeStateRef.current = {
      isResizing: false,
      resizingColumn: null,
      startX: 0,
      startWidth: 0,
      currentWidth: 0
    };

    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  }, [updateColumnWidths, handleResizeMove, settings?.preferences]); // Remove columnWidths dependency

  const handleResizeStart = useCallback((e: React.MouseEvent<HTMLDivElement>, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Column resize started for:', columnId);
    
    // Get current width directly from DOM or localStorage to avoid stale closure
    const currentLocalWidths = JSON.parse(localStorage.getItem('transactionColumnWidths') || '{}');
    const currentSettingsWidths = (settings?.preferences as any)?.columnWidths || {};
    const currentWidth = currentSettingsWidths[columnId] || currentLocalWidths[columnId] || '120px';
    
    const newState = {
      isResizing: true,
      resizingColumn: columnId,
      startX: e.clientX,
      startWidth: parseInt(currentWidth),
      currentWidth: parseInt(currentWidth)
    };
    
    // Update both React state and ref state
    setIsResizing(true);
    resizeStateRef.current = newState;

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd, settings?.preferences]); // Remove columnWidths dependency

  // Transaction actions
  const handleEdit = useCallback((transaction: LocalTransaction) => {
    setEditingTransaction(transaction);
  }, []);

  const handleCloseEdit = useCallback(async () => {
    setEditingTransaction(null);
    // Force refresh transactions data after editing to ensure UI shows latest updates
    try {
      console.log('Transaction edit closed, forcing refresh...');
      await refreshTransactions(true); // Reset = true to force fresh data from server
      console.log('Transaction refresh completed');
    } catch (error) {
      console.error('Error refreshing transactions after edit:', error);
    }
  }, [refreshTransactions]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      // Use the refreshTransactions method from the hook
      await refreshTransactions(true); // reset = true to clear existing data
      
      // Also call the onRefresh prop if provided for backward compatibility
      if (onRefresh) {
        await onRefresh();
      }
      
      toast.success(t('transactions.refreshSuccess', 'Transactions refreshed'));
    } catch (error) {
      console.error('Error refreshing transactions:', error);
      toast.error(t('transactions.refreshError', 'Failed to refresh transactions'));
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshTransactions, onRefresh, isRefreshing, t]);

  const handleBulkDelete = useCallback(async () => {
    if (!user?.id) {
      toast.error(t('errors.notAuthenticated'));
      return;
    }

    const transactionIds = Array.from(selectedTransactions);
    if (transactionIds.length === 0) {
      return;
    }

    try {
      console.log('Starting bulk delete for', transactionIds.length, 'transactions');
      
      // Set up progress tracking for operations with more than 10 transactions
      if (transactionIds.length > 10) {
        setBulkDeleteProgress({
          isDeleting: true,
          current: 0,
          total: transactionIds.length,
          errors: []
        });
      }

      // Define progress callback to update UI
      const onProgress = (current: number, total: number, errors: string[]) => {
        setBulkDeleteProgress({
          isDeleting: true,
          current,
          total,
          errors
        });
      };

      // Use the new bulk delete method from core store with progress callback
      const result = await coreTransactionStore.bulkDeleteTransactions(
        user.id, 
        transactionIds, 
        transactionIds.length > 10 ? onProgress : undefined
      );
      
      // Clear progress state
      setBulkDeleteProgress(null);

      // Clear selections and close dialog
      setSelectedTransactions(new Set());
      setShowDeleteAlert(false);

      // Force refresh the transaction list to ensure UI shows latest updates
      try {
        console.log('Bulk delete completed, refreshing transaction list...');
        if (user?.id) {
          await fetchTransactions(user.id, {
            page: 1,
            pageSize: 200,
            orderBy: 'date',
            orderDirection: 'desc'
          });
          console.log('Transaction list refreshed after bulk delete');
        }
      } catch (refreshError) {
        console.error('Error refreshing transaction list after bulk delete:', refreshError);
      }

      // Show appropriate success/error message
      if (result.deletedIds.length === transactionIds.length) {
        // All transactions deleted successfully
        toast.success(t('transactions.deleteSuccess', { count: result.deletedIds.length }));
      } else if (result.deletedIds.length > 0) {
        // Partial success
        toast.success(
          t('transactions.partialDeleteSuccess', {
            deleted: result.deletedIds.length,
            total: transactionIds.length,
            failed: result.failedIds.length
          }) || `Deleted ${result.deletedIds.length} of ${transactionIds.length} transactions. ${result.failedIds.length} failed.`
        );
        
        // Log errors for debugging
        if (result.errors.length > 0) {
          console.error('Bulk delete errors:', result.errors);
        }
      } else {
        // No transactions deleted
        toast.error(
          t('transactions.bulkDeleteFailed') || 'Failed to delete transactions. Please try again.'
        );
      }
      
      // Also call the onRefresh prop if provided for backward compatibility
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Error in bulk delete:', error);
      
      // Clear progress state and selections
      setBulkDeleteProgress(null);
      setSelectedTransactions(new Set());
      setShowDeleteAlert(false);
      
      toast.error(t('transactions.deleteError'));
    }
  }, [selectedTransactions, setSelectedTransactions, t, user?.id, coreTransactionStore, fetchTransactions, onRefresh]);

  const handleSingleDelete = useCallback(async (transactionId: string) => {
    if (!user?.id) {
      toast.error(t('errors.notAuthenticated'));
      return;
    }

    try {
      console.log('Deleting transaction:', transactionId);
      
      // Delete the transaction using the core transaction store
      await coreTransactionStore.deleteTransaction(user.id, transactionId);
      
      // Force refresh the transaction list to ensure UI shows latest updates
      try {
        console.log('Single transaction deleted, refreshing transaction list...');
        if (user?.id) {
          await fetchTransactions(user.id, {
            page: 1,
            pageSize: 200,
            orderBy: 'date',
            orderDirection: 'desc'
          });
          console.log('Transaction list refreshed after single delete');
        }
      } catch (refreshError) {
        console.error('Error refreshing transaction list after single delete:', refreshError);
      }
      
      // Show success message
      toast.success(t('transactions.deleteSuccess', { count: 1 }));
      
      // Also call the onRefresh prop if provided for backward compatibility
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error(t('transactions.deleteError'));
    }
  }, [fetchTransactions, onRefresh, t, user?.id, coreTransactionStore]);

  // Inject styles and cleanup on unmount
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = resizableStyles;
    document.head.appendChild(styleElement);

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
      // Don't clear local column widths on unmount as it may cause issues
      // setLocalColumnWidths({});
    };
  }, []); // Remove setLocalColumnWidths dependency

  return (
    <div className="transactions-container">
      {/* Header section */}
      <div className="transactions-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {accountId ? 
                (() => {
                  const account = accounts.find(acc => acc.id === accountId);
                  return t('transactions.accountTransactions', { account: account?.name || 'Unknown Account' });
                })() : 
                t('transactions.allTransactions')
              }
            </h2>
            {totalItems > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({transactions.length} out of {totalItems})
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('actions.refresh', 'Refresh')}
            </Button>
            {selectedTransactions.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMassUpdate(true)}
                >
                  {t('transactions.bulkEdit')} ({selectedTransactions.size})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteAlert(true)}
                  className="text-red-600 hover:text-red-700"
                >
                  {t('transactions.bulkDelete')} ({selectedTransactions.size})
                </Button>
              </>
            )}
            <ColumnSettings />
          </div>
        </div>
      </div>

      {/* Main content section */}
      {isMobile ? (
        // Mobile view with optimized cards for better performance
        <OptimizedMobileTransactionView
          transactions={transactions}
          selectedTransactions={selectedTransactions}
          isLoading={isLoading}
          hasMoreData={hasMoreData}
          pageSize={pageSize}
          loadingMoreText={loadingMoreText}
          onLoadMore={loadMore}
          t={t}
        />
      ) : (
        // Desktop view with optimized table for better performance
        <OptimizedTransactionTable
          transactions={transactions}
          columnOrder={columnOrder}
          transactionColumns={transactionColumns}
          columnWidths={columnWidths}
          selectedTransactions={selectedTransactions}
          totalItems={totalItems}
          isLoading={isLoading}
          hasMoreData={hasMoreData}
          pageSize={pageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          isColumnSorting={isColumnSorting}
          groupBy={groupBy}
          accounts={accounts}
          categories={categories}
          tags={tags}
          loadingMoreText={loadingMoreText}
          onToggleAllTransactions={toggleAllTransactions}
          onToggleTransactionSelection={toggleTransactionSelection}
          onSort={handleSort}
          onResizeStart={handleResizeStart}
          onGroupBy={setGroupBy}
          onEdit={handleEdit}
          onDelete={handleSingleDelete}
          onLoadMore={loadMore}
        />
      )}
      
      {/* Modals and dialogs */}
      {editingTransaction && (
        <EditTransaction
          isOpen={Boolean(editingTransaction)}
          onClose={handleCloseEdit}
          currentTransaction={editingTransaction as any}
        />
      )}
      
      {showMassUpdate && (
        <MassUpdateTransactions
          isOpen={showMassUpdate}
          onClose={() => {
            setShowMassUpdate(false);
            setSelectedTransactions(new Set());
          }}
          selectedTransactions={Array.from(selectedTransactions).map(id => 
            transactions.find(t => t.id === id)
          ).filter(Boolean) as any}
          onUpdated={async () => {
            // Refresh the transaction list after mass update completes
            try {
              console.log('Mass update completed, refreshing transaction list...');
              await refreshTransactions(true); // Force refresh with reset=true
              console.log('Transaction list refreshed after mass update');
            } catch (error) {
              console.error('Error refreshing transaction list after mass update:', error);
            }
          }}
        />
      )}
      
      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('transactions.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('transactions.deleteDialog.description', { count: selectedTransactions.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={bulkDeleteProgress?.isDeleting}
            >
              {bulkDeleteProgress?.isDeleting ? (
                <div className="flex items-center gap-2">
                  <div className="loading-spinner w-4 h-4"></div>
                  {t('transactions.deleting', 'Deleting...')}
                </div>
              ) : (
                t('actions.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Progress Dialog */}
      {bulkDeleteProgress?.isDeleting && (
        <AlertDialog open={true} onOpenChange={() => {}}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('transactions.bulkDeleteInProgress', 'Deleting Transactions')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('transactions.deletingProgress', {
                  current: bulkDeleteProgress.current,
                  total: bulkDeleteProgress.total
                }) || `Deleting ${bulkDeleteProgress.current} of ${bulkDeleteProgress.total} transactions...`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            
            <div className="space-y-4 px-6 pb-6">
              <div className="flex items-center gap-3">
                <div className="loading-spinner w-5 h-5"></div>
                <span className="text-sm text-muted-foreground">
                  Processing transactions...
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out" 
                  style={{ 
                    width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%` 
                  }}
                ></div>
              </div>

              {bulkDeleteProgress.errors.length > 0 && (
                <div className="text-sm text-red-600 dark:text-red-400">
                  <p className="font-medium">{t('transactions.someErrorsOccurred', 'Some errors occurred:')}</p>
                  <ul className="list-disc list-inside space-y-1 mt-1">
                    {bulkDeleteProgress.errors.slice(0, 3).map((error, index) => (
                      <li key={index} className="text-xs">{error}</li>
                    ))}
                    {bulkDeleteProgress.errors.length > 3 && (
                      <li className="text-xs">
                        {t('transactions.andMoreErrors', { count: bulkDeleteProgress.errors.length - 3 }) || 
                         `... and ${bulkDeleteProgress.errors.length - 3} more errors`}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}; 